import type { TenantCompanyProfile } from '@forq/platform-data';

/**
 * Maschinen-Belege als druckfertiges HTML im FTW-Layout -- Datenblatt (kundenfähiges Exposé mit Foto
 * und gruppierten Technikdaten), Preisschild (großer Verkaufspreis + Kennzahlen für Ausstellung) und
 * Inserat (Online-Anzeige mit fertigem, kopierbarem Anzeigentext für Portale wie mobile.de/Mascus/
 * TruckScout24/eigene Website). Gleicher Briefkopf/Fußzeile wie die Miet-/Rechnungsbelege.
 *
 * Kundenfähig: der Einkaufspreis (EK) taucht NICHT auf -- nur Verkaufspreis und Mietsätze.
 * Gedruckt/als PDF gespeichert wird über den Browser (Button im erzeugten HTML).
 */

export type MachineDocumentKind = 'datenblatt' | 'preisschild' | 'inserat';

export const machineDocumentLabels: Readonly<Record<MachineDocumentKind, string>> = {
  datenblatt: 'Datenblatt',
  preisschild: 'Preisschild',
  inserat: 'Inserat',
};

export interface MachineDocSpec {
  readonly label: string;
  readonly value: string;
}
export interface MachineDocGroup {
  readonly title: string;
  readonly specs: readonly MachineDocSpec[];
}
export interface MachineDocMachine {
  readonly number: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly constructionYear: number | null;
  readonly typeName: string;
  readonly location: string;
  readonly statusLabel: string;
  readonly imageUrl: string;
  readonly salesPrice: number;
  readonly rentalDailyRate: number;
  readonly rentalWeeklyRate: number;
  readonly rentalMonthlyRate: number;
}
export interface MachineDocContext {
  readonly machine: MachineDocMachine;
  readonly company: TenantCompanyProfile;
  readonly todayIso: string;
  /** Technische Felder, bereits mit Label + Einheit aufgelöst und nach Gruppen sortiert. */
  readonly groups: readonly MachineDocGroup[];
}

// --- Formatierung -----------------------------------------------------------

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
function money(value: number): string { return euro.format(value); }
function dateLabel(value: string): string {
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
}
function esc(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

/** Die günstigsten Mietsätze als kurze Zeile, oder '' wenn nicht vermietet. */
function rentalLine(machine: MachineDocMachine): string {
  const parts: string[] = [];
  if (machine.rentalDailyRate > 0) parts.push(`${money(machine.rentalDailyRate)} / Tag`);
  if (machine.rentalWeeklyRate > 0) parts.push(`${money(machine.rentalWeeklyRate)} / Woche`);
  if (machine.rentalMonthlyRate > 0) parts.push(`${money(machine.rentalMonthlyRate)} / Monat`);
  return parts.join(' · ');
}

// --- Briefkopf / Fußzeile (aus dem Firmenprofil) ---------------------------

function letterhead(company: TenantCompanyProfile): string {
  const identity = company.identity;
  const lh = company.letterhead;
  const slotLeft = lh.logoDataUrl
    ? `<img src="${esc(lh.logoDataUrl)}" alt="${esc(identity.name)}">`
    : `<div class="logo-name">${esc(identity.name || 'FORQ Maschinen')}</div>`;
  const slotCenter = lh.logoCenterDataUrl ? `<img src="${esc(lh.logoCenterDataUrl)}" alt="">` : '';
  const slotRight = lh.logoRightDataUrl
    ? `<img src="${esc(lh.logoRightDataUrl)}" alt="">`
    : `<div class="services">Handel&nbsp;&nbsp;Service<br>Ersatzteile&nbsp;&nbsp;Vermietung<br><span class="red">für Flurförderzeuge</span></div>`;
  return `
    <div class="hd">
      <div class="hd-slot hd-left">${slotLeft}</div>
      <div class="hd-slot hd-center">${slotCenter}</div>
      <div class="hd-slot hd-right">${slotRight}</div>
    </div>
    <hr class="hd-rule">`;
}

function footer(company: TenantCompanyProfile): string {
  const { identity, contact, bank } = company;
  const col = (heading: string, lines: (string | false)[]) =>
    `<div><span class="fh">${esc(heading)}</span>${lines.filter(Boolean).map((l) => esc(l as string)).join('\n')}</div>`;
  return `<div class="footer">
    ${col(identity.name || 'Firma', [identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()])}
    ${col('Kontakt', [contact.phone && `Tel.: ${contact.phone}`, contact.fax && `Fax: ${contact.fax}`, contact.email, contact.website])}
    ${col('Bankverbindung', [bank.bankName, bank.iban && `IBAN: ${bank.iban}`, bank.bic && `BIC: ${bank.bic}`])}
    ${col('Unternehmen', [identity.managingDirectors && `Geschäftsführer: ${identity.managingDirectors}`, identity.registerCourt && identity.register && `${identity.registerCourt} ${identity.register}`, identity.vatId && `USt-IdNr.: ${identity.vatId}`])}
  </div>`;
}

// --- Belegkörper ------------------------------------------------------------

function photo(machine: MachineDocMachine, cls: string): string {
  return machine.imageUrl
    ? `<div class="${cls}"><img src="${esc(machine.imageUrl)}" alt="${esc(`${machine.manufacturer} ${machine.model}`)}"></div>`
    : `<div class="${cls} photo--empty">kein Foto hinterlegt</div>`;
}

function datenblattBody(ctx: MachineDocContext): string {
  const m = ctx.machine;
  const identityRows: MachineDocSpec[] = [
    { label: 'Maschinenart', value: m.typeName },
    { label: 'Baujahr', value: m.constructionYear ? String(m.constructionYear) : '–' },
    { label: 'Serien-/Fabr.-Nr.', value: m.serialNumber || '–' },
    { label: 'Interne Nummer', value: m.number },
  ];
  const groupTables = ctx.groups
    .filter((group) => group.specs.length > 0)
    .map((group) => `
      <h2 class="sec">${esc(group.title)}</h2>
      <table class="box"><tbody>${group.specs.map((s) => `<tr><th>${esc(s.label)}</th><td>${esc(s.value)}</td></tr>`).join('')}</tbody></table>`)
    .join('');
  const rental = rentalLine(m);
  const priceRows: string[] = [];
  if (m.salesPrice > 0) priceRows.push(`<tr><td>Verkaufspreis</td><td class="num">${money(m.salesPrice)}</td></tr>`);
  if (rental) priceRows.push(`<tr><td>Mietsätze</td><td class="num">${esc(rental)}</td></tr>`);
  const priceBlock = priceRows.length
    ? `<h2 class="sec">Konditionen</h2>
       <table class="box price"><tbody>${priceRows.join('')}</tbody></table>
       <p class="note">Alle Preise verstehen sich netto zzgl. gesetzlicher Mehrwertsteuer. Angebot freibleibend, Zwischenverkauf vorbehalten.</p>`
    : '';
  return `
    <div class="db-hero">
      <div class="db-hero__info">
        <div class="db-hero__name">${esc(`${m.manufacturer} ${m.model}`.trim())}</div>
        <table class="box"><tbody>${identityRows.map((r) => `<tr><th>${esc(r.label)}</th><td>${esc(r.value)}</td></tr>`).join('')}</tbody></table>
      </div>
      ${photo(m, 'db-hero__photo')}
    </div>
    ${groupTables || '<p class="note">Für diesen Maschinentyp sind keine technischen Felder hinterlegt.</p>'}
    ${priceBlock}`;
}

function preisschildBody(ctx: MachineDocContext): string {
  const m = ctx.machine;
  const highlights = ctx.groups.flatMap((group) => group.specs).slice(0, 6);
  const rental = rentalLine(m);
  const priceText = m.salesPrice > 0 ? money(m.salesPrice) : 'Preis auf Anfrage';
  return `
    <div class="ps">
      <div class="ps__name">${esc(`${m.manufacturer} ${m.model}`.trim())}</div>
      <div class="ps__sub">${esc(m.typeName)}${m.constructionYear ? ` · Baujahr ${m.constructionYear}` : ''}</div>
      ${photo(m, 'ps__photo')}
      <div class="ps__price">${esc(priceText)}${m.salesPrice > 0 ? ' <span class="ps__vat">zzgl. MwSt.</span>' : ''}</div>
      ${rental ? `<div class="ps__rent">Mieten ab ${esc(rental.split(' · ')[0])}</div>` : ''}
      ${highlights.length ? `<div class="ps__specs">${highlights.map((s) => `<div><span>${esc(s.label)}</span><strong>${esc(s.value)}</strong></div>`).join('')}</div>` : ''}
    </div>`;
}

/**
 * Portalfertiger Anzeigentext -- zum Kopieren in mobile.de/Mascus/TruckScout24/eigene Website.
 * Kundenfähig: KEIN Einkaufspreis, nur Verkaufspreis/Mietsätze.
 */
function buildInseratText(m: MachineDocMachine, groups: readonly MachineDocGroup[], company: TenantCompanyProfile): string {
  const lines: string[] = [`${m.manufacturer} ${m.model}`.trim()];
  const sub = [m.typeName, m.constructionYear ? `Baujahr ${m.constructionYear}` : ''].filter(Boolean).join(', ');
  if (sub) lines.push(sub);
  lines.push('');
  const specs = groups.flatMap((group) => group.specs).slice(0, 10);
  if (specs.length > 0) {
    lines.push('Technische Daten:');
    for (const spec of specs) lines.push(`- ${spec.label}: ${spec.value}`);
    lines.push('');
  }
  if (m.serialNumber) lines.push(`Serien-/Fabriknummer: ${m.serialNumber}`);
  lines.push(`Preis: ${m.salesPrice > 0 ? `${money(m.salesPrice)} zzgl. MwSt.` : 'auf Anfrage'}`);
  const rental = rentalLine(m);
  if (rental) lines.push(`Mietoption: ${rental}`);
  lines.push('');
  lines.push('Zustand geprüft. Weitere Fotos und Informationen gerne auf Anfrage – Lieferung deutschlandweit möglich.');
  const contact = [company.contact.phone ? `Tel.: ${company.contact.phone}` : '', company.contact.email].filter(Boolean).join(' · ');
  const signature = [company.identity.name, contact].filter(Boolean).join(' · ');
  if (signature) { lines.push(''); lines.push(signature); }
  return lines.join('\n');
}

function inseratBody(ctx: MachineDocContext): string {
  const m = ctx.machine;
  const facts = ctx.groups.flatMap((group) => group.specs).slice(0, 8);
  const rental = rentalLine(m);
  const price = m.salesPrice > 0 ? money(m.salesPrice) : 'Preis auf Anfrage';
  const adText = buildInseratText(m, ctx.groups, ctx.company);
  return `
    <div class="ins">
      <div class="ins__head">
        <div>
          <div class="ins__name">${esc(`${m.manufacturer} ${m.model}`.trim())}</div>
          <div class="ins__sub">${esc(m.typeName)}${m.constructionYear ? ` · Baujahr ${m.constructionYear}` : ''}${m.location ? ` · Standort ${esc(m.location)}` : ''}</div>
        </div>
        <div class="ins__price">${esc(price)}${m.salesPrice > 0 ? '<span>zzgl. MwSt.</span>' : ''}</div>
      </div>
      ${photo(m, 'ins__photo')}
      ${facts.length ? `<div class="ins__facts">${facts.map((s) => `<div><span>${esc(s.label)}</span><strong>${esc(s.value)}</strong></div>`).join('')}</div>` : ''}
      ${rental ? `<div class="ins__rent">Mietoption: ${esc(rental)}</div>` : ''}
      <h2 class="sec">Anzeigentext <small>— fertig zum Einfügen in mobile.de, Mascus, TruckScout24 oder die eigene Website</small></h2>
      <textarea id="ad-desc" class="ad-desc" spellcheck="false">${esc(adText)}</textarea>
      <p class="note">Text prüfen und ggf. anpassen, dann oben „Beschreibung kopieren" klicken und im Portal einfügen. Der Einkaufspreis erscheint bewusst nicht.</p>
    </div>`;
}

// --- Shell (Kopf, Meta, Druck-CSS) -----------------------------------------

function shell(kind: MachineDocumentKind, ctx: MachineDocContext): string {
  const { company, machine, todayIso } = ctx;
  const accent = company.letterhead.accentColor || '#e30613';
  const meta = `
    <div class="doc-head">
      <div class="doc-title">${esc(machineDocumentLabels[kind].toUpperCase())}</div>
      <table class="meta">
        <tr><th>Maschine:</th><td>${esc(machine.number)}</td></tr>
        <tr><th>Datum:</th><td>${dateLabel(todayIso)}</td></tr>
        <tr><th>Status:</th><td>${esc(machine.statusLabel)}</td></tr>
      </table>
    </div>`;
  const band = kind === 'datenblatt'
    ? `<div class="band"><div class="sender">${esc(company.identity.name || '')}</div>${meta}</div><hr class="band-rule">`
    : '';
  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>${esc(machineDocumentLabels[kind])} ${esc(machine.number)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); position: relative; }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; } .hd-center { justify-content: center; } .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 10mm; }
  .sender { font-size: 12px; color: #444; font-weight: 700; }
  .doc-head { min-width: 70mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 17px; font-weight: 800; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  .band-rule { border: 0; border-top: 1px solid #bbb; margin: 6px 0 0; }
  h2.sec { font-size: 13.5px; font-weight: 700; margin: 7mm 0 3mm; color: #111; }
  table.box { width: 100%; border-collapse: collapse; border: 1px solid #ccc; }
  table.box th { text-align: left; color: var(--label); font-weight: 700; padding: 5px 8px; width: 42%; vertical-align: top; border: 1px solid #e2e2e2; background: #fafafa; }
  table.box td { padding: 5px 8px; vertical-align: top; border: 1px solid #e2e2e2; }
  table.box td.num { text-align: right; font-weight: 700; }
  table.box.price th, table.box.price td { font-size: 13px; }
  .note { font-size: 10px; color: #444; margin: 3mm 0; text-align: justify; }
  /* Datenblatt-Hero: Info links, Foto rechts */
  .db-hero { display: flex; gap: 8mm; align-items: flex-start; margin-top: 8mm; }
  .db-hero__info { flex: 1; min-width: 0; }
  .db-hero__name { font-size: 20px; font-weight: 800; color: var(--accent); margin-bottom: 4mm; }
  .db-hero__photo { flex: none; width: 78mm; }
  .db-hero__photo img { width: 100%; max-height: 58mm; object-fit: contain; border: 1px solid #e2e2e2; }
  .photo--empty { display: flex; align-items: center; justify-content: center; height: 40mm; color: #aaa; font-size: 10px; border: 1px dashed #ccc; }
  /* Preisschild */
  .ps { text-align: center; padding-top: 6mm; }
  .ps__name { font-size: 30px; font-weight: 800; color: var(--accent); }
  .ps__sub { font-size: 15px; color: #444; margin-top: 2mm; }
  .ps__photo { margin: 8mm auto; width: 150mm; }
  .ps__photo img { width: 100%; max-height: 95mm; object-fit: contain; }
  .ps__photo.photo--empty { height: 60mm; width: 150mm; }
  .ps__price { font-size: 40px; font-weight: 800; margin-top: 4mm; }
  .ps__vat { font-size: 15px; font-weight: 500; color: #666; }
  .ps__rent { font-size: 16px; color: #333; margin-top: 3mm; }
  .ps__specs { display: flex; flex-wrap: wrap; justify-content: center; gap: 4mm 10mm; margin-top: 8mm; }
  .ps__specs div { font-size: 13px; }
  .ps__specs span { display: block; color: #888; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
  .ps__specs strong { font-size: 15px; }
  /* Inserat / Online-Anzeige */
  .ins__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8mm; margin-top: 8mm; }
  .ins__name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .ins__sub { font-size: 12px; color: #444; margin-top: 2mm; }
  .ins__price { font-size: 24px; font-weight: 800; white-space: nowrap; text-align: right; }
  .ins__price span { display: block; font-size: 11px; font-weight: 500; color: #666; }
  .ins__photo { margin: 6mm 0; }
  .ins__photo img { width: 100%; max-height: 92mm; object-fit: contain; border: 1px solid #e2e2e2; }
  .ins__photo.photo--empty { height: 50mm; }
  .ins__facts { display: flex; flex-wrap: wrap; gap: 3mm 9mm; margin: 4mm 0; }
  .ins__facts div { font-size: 12px; }
  .ins__facts span { display: block; color: #888; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; }
  .ins__facts strong { font-size: 13px; }
  .ins__rent { font-size: 13px; color: #333; margin: 2mm 0 0; }
  .ad-desc { width: 100%; min-height: 72mm; font: inherit; font-size: 11px; line-height: 1.55; padding: 9px 11px; border: 1px solid #ccc; border-radius: 4px; resize: vertical; color: #1a1a1a; }
  .footer { margin-top: 14mm; border-top: 1.5px solid var(--accent); padding-top: 5px; display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
    h2.sec { break-after: avoid; break-inside: avoid; }
    table.box, .db-hero, .ps__specs { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar">${kind === 'inserat' ? '<button type="button" onclick="copyDesc(this)">Beschreibung kopieren</button>' : ''}<button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    ${letterhead(company)}
    ${band}
    ${kind === 'datenblatt' ? datenblattBody(ctx) : kind === 'preisschild' ? preisschildBody(ctx) : inseratBody(ctx)}
    ${footer(company)}
  </div>
  ${kind === 'inserat' ? `<script>
    function copyDesc(btn){var ta=document.getElementById('ad-desc');if(!ta){return;}ta.focus();ta.select();var ok=false;try{ok=document.execCommand('copy');}catch(e){ok=false;}if(!ok&&navigator.clipboard){navigator.clipboard.writeText(ta.value);}var prev=btn.textContent;btn.textContent='Kopiert \\u2713';setTimeout(function(){btn.textContent=prev;},1500);}
  </script>` : ''}
</body></html>`;
}

/** Das vollständige HTML eines Maschinen-Belegs -- zum Anzeigen im iframe (srcDoc) und Drucken. */
export function renderMachineDocumentHtml(kind: MachineDocumentKind, ctx: MachineDocContext): string {
  return shell(kind, ctx);
}
